<script setup>
import { computed, ref } from 'vue';
import { useMessageFormatter } from 'shared/composables/useMessageFormatter';
import SandboxCitations from './SandboxCitations.vue';

const props = defineProps({
  content: {
    type: String,
    required: true,
  },
  chunks: {
    type: Array,
    default: () => [],
  },
  rating: {
    type: String,
    default: null,
    validator: value => [null, 'up', 'down'].includes(value),
  },
});

const emit = defineEmits(['feedback']);

const { formatMessage } = useMessageFormatter();
const citationsRef = ref(null);

// The reply contains inline [n] markers matching a chunk's chunk_index.
// Rendered as a clickable <sup> — class-based encoding (not a data
// attribute) so it reliably survives HTML sanitization.
const formattedMessage = computed(() => {
  const html = formatMessage(props.content);
  return html.replace(
    /\[(\d+)\]/g,
    '<sup class="citation-marker citation-index-$1 text-woot-500 cursor-pointer">$1</sup>'
  );
});

const handleMessageClick = event => {
  const marker = event.target.closest('.citation-marker');
  if (!marker) return;
  const match = [...marker.classList].find(c =>
    c.startsWith('citation-index-')
  );
  if (!match) return;
  const chunkIndex = Number(match.replace('citation-index-', ''));
  citationsRef.value?.expandAndScrollTo(chunkIndex);
};
</script>

<template>
  <div class="w-full mb-4 flex items-center justify-start">
    <div
      class="px-4 py-3 bg-white dark:bg-slate-800 max-w-2xl text-slate-700 dark:text-slate-200 leading-6 text-sm rounded-md inline-block border border-slate-100 dark:border-slate-700"
    >
      <div
        v-dompurify-html="formattedMessage"
        class="message-content"
        @click="handleMessageClick"
      />
      <SandboxCitations ref="citationsRef" :chunks="chunks" />
      <div class="flex items-center gap-1 mt-2">
        <!-- No thumb-up/down icon exists in the FluentIcon set — inline SVG
             rather than adding a new icon dependency for two icons. -->
        <button
          type="button"
          class="p-1 rounded hover:bg-slate-50 dark:hover:bg-slate-700"
          :class="rating === 'up' ? 'text-woot-500' : 'text-slate-400'"
          @click="emit('feedback', 'up')"
        >
          <svg
            width="14"
            height="14"
            viewBox="0 0 20 20"
            fill="none"
            stroke="currentColor"
            stroke-width="1.5"
          >
            <path
              d="M2 18h3V9H2v9zM18 8.5c0-.83-.67-1.5-1.5-1.5H12l.7-3.5c.1-.5-.03-1-.36-1.36A1.5 1.5 0 0 0 11.24 2L7 6.5V18h9c.7 0 1.3-.47 1.46-1.15l1.44-6.5c.06-.28.1-.57.1-.85z"
            />
          </svg>
        </button>
        <button
          type="button"
          class="p-1 rounded hover:bg-slate-50 dark:hover:bg-slate-700"
          :class="rating === 'down' ? 'text-woot-500' : 'text-slate-400'"
          @click="emit('feedback', 'down')"
        >
          <svg
            width="14"
            height="14"
            viewBox="0 0 20 20"
            fill="none"
            stroke="currentColor"
            stroke-width="1.5"
          >
            <path
              transform="translate(0 20) scale(1 -1)"
              d="M2 18h3V9H2v9zM18 8.5c0-.83-.67-1.5-1.5-1.5H12l.7-3.5c.1-.5-.03-1-.36-1.36A1.5 1.5 0 0 0 11.24 2L7 6.5V18h9c.7 0 1.3-.47 1.46-1.15l1.44-6.5c.06-.28.1-.57.1-.85z"
            />
          </svg>
        </button>
      </div>
    </div>
  </div>
</template>

<style scoped>
.message-content :deep(p:last-child) {
  margin-bottom: 0;
}
</style>
